<script>  import { onMount } from 'svelte';
  import Chart from 'chart.js/auto';
  import annotationPlugin from 'chartjs-plugin-annotation';
  import { selectedStudy } from '$stores/selectionStore';
  import { studyProperties, fetchStudyProperties } from '$stores/configStore';
  import { theme } from '$stores/themeStore';
  import { createChartPrintHandler, getTextColor } from '$utils/chartPrintUtils';
  import { get } from 'svelte/store';
  Chart.register(annotationPlugin);

  /** Break line/fill when consecutive samples are farther apart than this (ms). */
  const MAX_GLUCOSE_GAP_MS = 20 * 60 * 1000;

  /**
   * Sort by time and insert { y: null } between points so Chart.js does not bridge long gaps.
   * Relying on spanGaps alone fails if the API returns readings out of chronological order.
   */
  function buildDailyProfilePoints(rows) {
    if (!rows?.length) return [];
    const sorted = [...rows].sort(
      (a, b) => new Date(a.read).getTime() - new Date(b.read).getTime()
    );
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
      const r = sorted[i];
      const t = new Date(r.read).getTime();
      if (i > 0) {
        const prevT = new Date(sorted[i - 1].read).getTime();
        if (t - prevT > MAX_GLUCOSE_GAP_MS) {
          out.push({ x: new Date(prevT + (t - prevT) / 2), y: null });
        }
      }
      out.push({ x: new Date(r.read), y: r.value });
    }
    return out;
  }

  export let readings = [];
  export let date = '';

  $: formattedDate = date ? new Date(date.replace(/-/g, '/')).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  }) : '';  let canvas;
  let chart;
  let printHandler;

  $: if (canvas && readings) {
    drawChart();
  }
  onMount(async () => {
    // Fetch study properties to get glucose thresholds
    if ($selectedStudy?._id) {
      await fetchStudyProperties($selectedStudy._id);
    }

    // Initialize print handler
    printHandler = createChartPrintHandler(
      () => {
        if (chart && readings) {
          drawChart();
        }
      },
      () => chart && readings
    );

    // Cleanup function
    return () => {
      if (printHandler) {
        printHandler.cleanup();
      }
    };
  });

  // Subscribe to theme changes to redraw chart
  $: if ($theme && chart) {
    drawChart();
  }
  function drawChart() {
    if (chart) {
      chart.destroy();
    }

    // Create start and end of day timestamps based on the first reading's date
    const firstReadingDate = readings[0]?.read ? new Date(readings[0].read) : new Date(date.replace(/-/g, '/'));
    const startOfDay = new Date(firstReadingDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(firstReadingDate);
    endOfDay.setHours(23, 59, 59, 999);    //console.log('startOfDay:', startOfDay);
    //console.log('endOfDay:', endOfDay);

    //console.log('first:', readings[0].read);
    //console.log('last:', readings[readings.length - 1].read);

    const dataPoints = buildDailyProfilePoints(readings);

    // Use the print handler's isPrintMode store to determine text color
    const isPrintMode = printHandler ? get(printHandler.isPrintMode) : false;
    const textColor = getTextColor(isPrintMode);
    
    // Use study properties for thresholds if available
    const highThreshold = $studyProperties?.glucoseParameters?.thresholds?.high || 180;
    const lowThreshold = $studyProperties?.glucoseParameters?.thresholds?.low || 70;

    chart = new Chart(canvas, {
      type: 'line',
      data: {
        datasets: [{
          label: 'Glucose Readings',
          data: dataPoints,
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1.5,
          fill: {
            target: {
              value: (ctx) => {
                const value = ctx.p0.parsed.y;
                if (value == null || !Number.isFinite(value)) return false;
                if (value > highThreshold) return highThreshold;
                if (value < lowThreshold) return lowThreshold;
                return false; // No fill for values within range
              }
            },
            above: 'rgba(255, 193, 7, 0.3)', // Yellow/amber for high values
            below: 'rgba(220, 53, 69, 0.3)'  // Red for low values
          },
          segment: {
            borderColor: ctx => {
              const value = ctx.p1.parsed.y;
              if (value == null || !Number.isFinite(value)) {
                return 'rgba(75, 192, 192, 1)';
              }
              if (value > highThreshold) return 'rgba(255, 193, 7, 0.8)';
              if (value < lowThreshold) return 'rgba(220, 53, 69, 0.8)';
              return 'rgba(75, 192, 192, 1)';
            }
          },
          tension: 0.4,
          pointRadius: 0,
          spanGaps: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        ...(isPrintMode ? { animation: false, animations: false } : {}),
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          },
          title: {
            display: true,
            text: formattedDate,
            color: textColor,
            font: {
              size: 12,
              weight: 'light'
            }
          },
          annotation: {
            annotations: {
              line1: {
                type: 'line',
                yMin: highThreshold,
                yMax: highThreshold,
                borderColor: 'red',
                borderWidth: 0.5,
              },
              line2: {
                type: 'line',
                yMin: lowThreshold,
                yMax: lowThreshold,
                borderColor: 'orange',
                borderWidth: 0.5,
              }
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH:mm'
              }
            },
            min: startOfDay,
            max: endOfDay,
            title: {
              display: false
            },
            ticks: {
              maxTicksLimit: undefined,
              color: textColor,
              font: {
                size: 10,
                weight: 'light'
              },
              maxRotation: 0,
              minRotation: 0,
              callback: function(value) {
                const hour = new Date(value).getHours();
                // Show label only at 02:00 and every 4 hours after that
                return (hour - 2) % 4 === 0 ? hour.toString().padStart(2, '0') : '';
              }
            }          },
          y: {
            min: 40,
            max: 410,
            ticks: {
              display: false,
              color: textColor
            }
          }
        }
      }
    });
  }
</script>

<div class="daily-profile">
  <canvas
    bind:this={canvas}
    class="bg-white dark:bg-gray-800/20 rounded shadow-sm print:bg-white print:dark:bg-white"
  ></canvas>
</div>

<style>
  .daily-profile {
    display: inline-block;
    margin: 5px;
    width: 250px; /* default for iPad */
    height: 130px;
  }

  /* For desktop screens (1200px and up) */
  @media (min-width: 1200px) {
    .daily-profile {
      width: 185px; /* smaller size to fit 7 charts per row on desktop */
      height: 110px;
    }
  }
</style>